import React, { useContext, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { IconButton, TextField, Tooltip } from '@mui/material';
import { Add, Cancel, Edit, Save, FindInPage } from '@mui/icons-material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { AppContext } from '../../../../../RoutesWithAuth';
import axios from '../../../../common/AxiosConfig';



const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
        maxWidth: '100%',
        minWidth: 0
    },
    tableWrapper: {
        marginTop: '2px',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
            width: '8px',
            height: '10px'
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1'
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#a8a8a8',
            borderRadius: '4px',
            '&:hover': {
                background: '#888'
            },
        },
        '& table': {
            width: '100%',
            minWidth: 0,
            tableLayout: 'fixed',
            borderCollapse: 'separate'
        },
        '& th, & td': {
            boxSizing: 'border-box',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            lineHeight: 1.4,
            verticalAlign: 'middle'
        },
        '& thead': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            background: '#C4D2E2',
        },
        '& th': {
            padding: '8px 4px',
            //textAlign: 'center',
            color: '#202124',
            fontWeight: 600,
            fontSize: '0.85rem',
            borderRight: '1px solid #c8cbce',
            letterSpacing: '0.3px',
            background: '#C4D2E2',
            '&:last-child': {
                borderRight: 'none',
            },
        },
        '& .editColumn': {
            padding: '2px 4px',
            textAlign: 'center',
            whiteSpace: 'normal'
        },
        '& thead th': {
            textAlign: 'left',
            verticalAlign: 'top',
            padding: '2px 4px'
        },
        '& .MuiInputBase-root': {
            width: '100%',
            minWidth: 0,
            fontSize: '0.85rem'
        },
        '& .MuiTextField-root': {
            width: '100%',
            minWidth: 0
        },
        '& .MuiInputBase-input': {
            minWidth: 0,
            padding: '6px 4px',
            boxSizing: 'border-box',
            overflowWrap: 'anywhere'
        },
        '& .MuiInputBase-multiline': {
            padding: 0
        },
        '& tbody tr': {
            height: '4vh',
            background: '#f7fafd',
            fontWeight: 600,
            borderBottom: '1px solid #edf2f7',
            transition: 'background-color 0.2s ease',
            //background: '#dee2e6',
            '&:hover': {
                background: '#f0f7ff',
            },
        },
        '& td': {
            padding: '8px 4px',
            textAlign: 'left',
            color: '#334155',
            fontSize: '0.85rem',
            fontWeight: 600,
            borderRight: '1px solid #edf2f7',
            '&:last-child': {
                borderRight: 'none',
            },
        },
    },
    saveButton: {
        color: '#62A9D4'
    },
    editButton: {
        color: '62A9D4'
    },
    cancelButton: {
        color: '#C00000'
    },
    requiredMark: {
        color: '#d32f2f',
        marginLeft: '2px'
    },
    errorMessage: {
        color: '#d32f2f'
    }
}));


const COLUMNS = [
    { key: 'reviewer_name', label: 'Legal Reviewer Name', width: '11%', inputType: 'text', required: true },
    { key: 'review_date', label: 'Review Date', width: '7%', inputType: 'date', required: true },
    { key: 'status', label: 'Status', width: '8%', inputType: 'text' },
    { key: 'approval_date', label: 'Approval Date', width: '8%', inputType: 'date' },
    { key: 'legal_comments', label: 'Legal Comments / Notes', width: '9%', inputType: 'text' },
    { key: 'upload_approval_document', label: 'Upload Approval Document', width: '9%' },
    { key: 'document_name', label: 'Document Name', width: '14%' },
    { key: 'view_approval_document', label: 'View Approval Document', width: '10%' },
    { key: 'edit', label: 'Edit', width: '6%', className: 'editColumn' },
];

const REQUIRED_COLUMNS = COLUMNS.filter(column => column.required);

const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getDocumentUrl = row => row.view_approval_document || row.view_url;
const hasDocument = row => Boolean(row.document_name || row.file_name || getDocumentUrl(row));

const LegalApprovalTable = ({ taskId }) => {
    const classes = useStyles();
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const [rows, setRows] = useState([]);
    const [editingRow, setEditingRow] = useState(null);
    const [draft, setDraft] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [showRequiredErrors, setShowRequiredErrors] = useState(false);
    const fileInput = useRef(null);
    const localDocumentUrls = useRef(new Set());
    const pendingUpload = useRef(null);

    useEffect(() => {
        const urls = localDocumentUrls.current;
        return () => {
            urls.forEach(url => URL.revokeObjectURL(url));
            urls.clear();
        };
    }, [taskId, authToken]);

    useEffect(() => {
        let isActive = true;
        pendingUpload.current = null;
        setEditingRow(null);
        setDraft(null);
        setError('');
        setIsLoading(false);
        if (!taskId) return;

        setRows([]);
        setIsLoading(true);
        axios.get(`centra/privacy-task/${encodeURIComponent(taskId)}/approvals`, {
            headers: { Authorization: authToken }
        }).then((response) => {
            const approvals = response.data?.approvals ?? response.data ?? [];
            if (!Array.isArray(approvals)) {
                throw new Error('Invalid approvals response.');
            }
            if (isActive) setRows(approvals);
        }).catch((err) => {
            if (isActive) {
                setError(err?.response?.data?.message || err?.message || 'Unable to load approvals.');
            }
        }).finally(() => {
            if (isActive) setIsLoading(false);
        });

        return () => {
            isActive = false;
        };
    }, [taskId, authToken]);

    const cancelEdit = () => {
        pendingUpload.current = null;
        const url = draft && getDocumentUrl(draft);
        if (url && url !== getDocumentUrl(rows[editingRow] || {}) && localDocumentUrls.current.has(url)) {
            URL.revokeObjectURL(url);
            localDocumentUrls.current.delete(url);
        }
        setEditingRow(null);
        setDraft(null);
        setShowRequiredErrors(false);
    };

    const attachDocument = event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !draft || isSaving) return;
        if (hasDocument(draft)) {
            setError('Only one document can be attached.');
            return;
        }
        try {
            const url = URL.createObjectURL(file);
            localDocumentUrls.current.add(url);
            setDraft(current => ({
                ...current,
                document_file: file,
                document_name: file.name,
                view_approval_document: url
            }));
            setError('');
        } catch (err) {
            setError(err?.message || 'Unable to attach document.');
        }
    };

    const saveEdit = async () => {
        if (!taskId || !draft || isSaving) return;

        const isNewRow = editingRow === rows.length;
        const approvalId = draft.approval_id ?? draft.id;
        const hasApprovalId = approvalId !== undefined && approvalId !== null && approvalId !== '';
        if (!isNewRow && !hasApprovalId) {
            setError('Unable to save approval: approval ID is missing.');
            return;
        }
        const missingFields = REQUIRED_COLUMNS.filter(column => !String(draft[column.key] ?? '').trim());
        if (missingFields.length) {
            setShowRequiredErrors(true);
            setError(`Please enter ${missingFields.map(column => column.label).join(' and ')}.`);
            return;
        }
        const today = getTodayDate();
        const futureDates = COLUMNS.filter(column =>
            column.inputType === 'date' && draft[column.key] && draft[column.key] > today
        );
        if (futureDates.length) {
            setError(`${futureDates.map(column => column.label).join(' and ')} cannot be in the future.`);
            return;
        }
        setShowRequiredErrors(false);
        const payload = {
            reviewer_name: draft.reviewer_name.trim(),
            status: draft.status || '',
            review_date: draft.review_date || '',
            approval_date: draft.approval_date || '',
            legal_comments: draft.legal_comments || ''
        };
        setIsSaving(true);
        setError('');
        const approvalsUrl = `centra/privacy-task/${encodeURIComponent(taskId)}/approvals`;
        const requestConfig = { headers: { Authorization: authToken } };
        const file = draft.document_file;
        try {
            if (file && !pendingUpload.current) {
                const documentResponse = await axios.post(
                    hasApprovalId ? `${approvalsUrl}/${encodeURIComponent(approvalId)}/document` : `${approvalsUrl}/document`,
                    { file_name: file.name, file_size: file.size },
                    requestConfig
                );
                const uploadId = documentResponse.data?.upload_id;
                if (uploadId === undefined || uploadId === null || uploadId === '') {
                    throw new Error('The server did not return an upload ID for the approval document.');
                }
                const uploadForm = documentResponse.data?.upload_form;
                const fields = uploadForm?.upload?.fields;
                if (uploadForm?.file_name !== file.name || !uploadForm.upload?.url ||
                    !fields || typeof fields !== 'object' || Array.isArray(fields)) {
                    throw new Error('The server returned incomplete document upload instructions.');
                }
                pendingUpload.current = { upload_id: uploadId, upload_form: uploadForm, uploaded: false };
            }
            if (file) {
                if (!pendingUpload.current.uploaded) {
                    const { upload_form: uploadForm } = pendingUpload.current;
                    const uploadData = new FormData();
                    Object.entries(uploadForm.upload.fields).forEach(([key, value]) => {
                        uploadData.append(key, value);
                    });
                    uploadData.append('file', file);
                    await axios.post(uploadForm.upload.url, uploadData);
                    pendingUpload.current.uploaded = true;
                }
                payload.upload_id = pendingUpload.current.upload_id;
            }
            const saveResponse = hasApprovalId
                ? await axios.put(`${approvalsUrl}/${encodeURIComponent(approvalId)}`, payload, requestConfig)
                : await axios.post(approvalsUrl, payload, requestConfig);
            const updated = saveResponse.data?.approval ?? saveResponse.data;
            const savedId = updated?.approval_id ?? updated?.id ?? approvalId;
            // Retrying after a partial failure must update this approval instead of creating a duplicate.
            if (savedId !== undefined && savedId !== null && savedId !== '') {
                setDraft(current => ({ ...current, approval_id: savedId }));
            }

            const savedRow = { ...draft, ...updated };
            if (file) {
                pendingUpload.current = null;

                savedRow.document_name = updated?.document_name || file.name;
                if (updated?.view_url) {
                    savedRow.view_url = updated.view_url;
                    delete savedRow.view_approval_document;
                    const localUrl = getDocumentUrl(draft);
                    if (localDocumentUrls.current.has(localUrl)) {
                        URL.revokeObjectURL(localUrl);
                        localDocumentUrls.current.delete(localUrl);
                    }
                }
            }
            delete savedRow.document_file;
            setRows(previousRows => isNewRow
                ? [...previousRows, savedRow]
                : previousRows.map((row, rowIndex) => rowIndex === editingRow ? savedRow : row));
            setEditingRow(null);
            setDraft(null);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Unable to save approval.');
        } finally {
            setIsSaving(false);
        }
    };

    const renderCell = (row, rowIndex, column) => {
        const isEditing = editingRow === rowIndex;
        const isNewRow = rowIndex === rows.length;
        const currentRow = isEditing ? draft : row;
        if (column.key === 'upload_approval_document') {
            const attached = hasDocument(currentRow);
            if (!isEditing && !attached) return null;
            return (
                <>
                    {isEditing && (
                        <input
                            ref={fileInput}
                            type="file"
                            hidden
                            aria-label="Choose approval document"
                            disabled={attached || isSaving}
                            onChange={attachDocument}
                        />
                    )}
                    <Tooltip title={attached ? 'Only one document can be attached.' : 'Upload approval document'}>
                        <span>
                            <IconButton
                                size="small"
                                aria-label="Upload approval document"
                                disabled={attached || isSaving}
                                onClick={() => fileInput.current.click()}
                            >
                                <FileUploadOutlinedIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </>
            );
        }
        if (column.key === 'document_name') return currentRow.document_name || currentRow.file_name;
        if (column.key === 'view_approval_document') {
            const url = getDocumentUrl(currentRow);
            return url ? (
                <Tooltip title="View approval document">
                    <IconButton
                        size="small"
                        aria-label={`View ${currentRow.document_name || currentRow.file_name || 'approval document'}`}
                        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                    >
                        <FindInPage fontSize="small" />
                    </IconButton>
                </Tooltip>
            ) : null;
        }
        if (column.key === 'edit') {
            return isEditing ? (
                <>
                    <Tooltip title="Save">
                        <IconButton className={classes.saveButton} size="small" aria-label={`Save ${row.reviewer_name}`} onClick={saveEdit} disabled={isSaving}>
                            <Save fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                        <IconButton className={classes.cancelButton} size="small" aria-label={`Cancel ${row.reviewer_name}`} onClick={cancelEdit} disabled={isSaving}>
                            <Cancel fontSize="small" color="error" />
                        </IconButton>
                    </Tooltip>
                </>
            ) : (
                <Tooltip title={isNewRow ? 'Add approval' : 'Edit'}>
                    <span>
                        <IconButton
                            className={classes.editButton}
                            size="small"
                            aria-label={isNewRow ? 'Add approval' : `Edit ${row.reviewer_name}`}
                            disabled={editingRow !== null || !taskId}
                            onClick={() => { setError(''); setEditingRow(rowIndex); setDraft({ ...row }); }}
                        >
                            {isNewRow ? <Add fontSize="small" /> : <Edit fontSize="small" />}
                        </IconButton>
                    </span>
                </Tooltip>
            );
        }
        if (isEditing && column.inputType) {
            const value = draft[column.key] || '';
            const isMissing = column.required && showRequiredErrors && !String(value).trim();
            return (
                <TextField
                    size="small"
                    fullWidth
                    required={column.required}
                    error={isMissing}
                    type={column.inputType}
                    multiline={column.inputType === 'text'}
                    minRows={1}
                    value={value}
                    disabled={isSaving}
                    inputProps={{
                        'aria-label': `${column.label} for ${row.reviewer_name}`,
                        'aria-required': column.required || undefined,
                        max: column.inputType === 'date' ? getTodayDate() : undefined
                    }}
                    onChange={event => {
                        const value = event.target.value;
                        setDraft(current => ({ ...current, [column.key]: value }));
                    }}
                />
            );
        }
        return row[column.key];
    };

    return (
        <div className={classes.root}>
            {/* {isSaving && <p role="status">Saving data element...</p>} */}
            {isLoading && <p role="status">Loading approvals...</p>}
            <div className={`tableWrapper ${classes.tableWrapper}`}>
                <table>
                    <colgroup>
                        {COLUMNS.map(column => <col key={column.label} style={{ width: column.width }} />)}
                    </colgroup>
                    <thead>
                        <tr>
                            {COLUMNS.map((column) => (
                                <th scope="col" className={column.className} key={column.label}>
                                    {column.label}
                                    {column.required && <span className={classes.requiredMark} aria-hidden="true">*</span>}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={row.approval_id ?? row.id ?? rowIndex}>
                                {COLUMNS.map((column) => (
                                    <td className={column.className} key={column.label}>{renderCell(row, rowIndex, column)}</td>
                                ))}
                            </tr>
                        ))}
                        {!isLoading && (
                            <tr key="new-approval">
                                {COLUMNS.map(column => (
                                    <td className={column.className} key={column.label}>{renderCell({}, rows.length, column)}</td>
                                ))}
                            </tr>
                        )}
                        <tr><td colSpan={COLUMNS.length}></td></tr>
                        <tr><td colSpan={COLUMNS.length}></td></tr>
                    </tbody>
                </table>
            </div>
            {error && <p role="alert" className={classes.errorMessage}>{error}</p>}
        </div>
    )
}

export default LegalApprovalTable;